<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0 font-serif text-body-emphasis">
        {{ t("ecolabels.certificates.heading") }}
      </h4>
      <div class="d-flex align-items-center gap-2">
        <MultiSelect
          v-model="visibleColumns"
          :options="columnOptions"
          optionLabel="label"
          optionValue="value"
          :placeholder="t('ecolabels.select_columns')"
          class="column-selector"
          display="chip"
        >
          <template #value="{ value }">
            <span class="text-body-tertiary">
              <font-awesome-icon icon="table-columns" class="me-1" />
              {{
                t("ecolabels.columns_selected", { count: value?.length ?? 0 })
              }}
            </span>
          </template>
        </MultiSelect>
        <button class="btn btn-primary btn-sm" @click="$emit('open-upload')">
          <font-awesome-icon icon="upload" class="me-1" />
          {{ t("ecolabels.certificates.upload_statement") }}
        </button>
      </div>
    </div>

    <div class="card border-translucent">
      <div class="card-body p-0 overflow-x-auto">
        <DataTable
          v-model:filters="tableFilters"
          :value="tableRows"
          dataKey="id"
          filterDisplay="row"
          class="ecolabel-certificates-datatable"
          :scrollable="true"
          :scrollHeight="statements.length > 15 ? '600px' : undefined"
          :virtualScrollerOptions="
            statements.length > 15
              ? { itemSize: 46, numToleratedItems: 20 }
              : undefined
          "
          :loading="loading"
          sortMode="single"
          removableSort
          :tableStyle="{ minWidth: tableMinWidth, width: 'max-content' }"
        >
          <template #empty>
            <div class="text-center py-4 text-muted">
              {{ t("ecolabels.certificates.no_statements") }}
            </div>
          </template>
          <template #loading>
            <LoadingIndicatorComponent />
          </template>

          <Column
            v-if="isColVisible('file_name')"
            field="file_name"
            :header="t('ecolabels.certificates.file_name')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 16rem"
          >
            <template #body="{ data }">
              <div class="fw-semibold">{{ data.file_name }}</div>
              <small v-if="data.beneficiary_name" class="text-muted">
                {{ data.beneficiary_name }}
              </small>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('ecolabels.filters.search_placeholder')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>

          <Column
            v-if="isColVisible('created_at')"
            field="created_at"
            :header="t('ecolabels.certificates.uploaded')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 10rem"
          >
            <template #body="{ data }">
              {{ formatDateTimeIntoDate(data.created_at) }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('ecolabels.filters.search')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>

          <Column
            v-if="isColVisible('seller_company')"
            field="seller_company"
            :header="t('ecolabels.upload_wizard.seller_company')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 12rem"
          >
            <template #body="{ data }">
              <span>{{ data.company_name || "-" }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('ecolabels.filters.search')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>

          <Column
            v-if="isColVisible('beneficiary_name')"
            field="beneficiary_name"
            :header="t('ecolabels.upload_wizard.beneficiary_name')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 12rem"
          >
            <template #body="{ data }">
              <span>{{ data.beneficiary_name || "-" }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('ecolabels.filters.search')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>

          <Column
            v-if="isColVisible('registry_code')"
            field="registry_code"
            :header="t('ecolabels.upload_wizard.registry_code')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 10rem"
          >
            <template #body="{ data }">
              <span>{{ data.registry_code || "-" }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('ecolabels.filters.search')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>

          <Column
            v-if="isColVisible('country')"
            field="country"
            :header="t('ecolabels.upload_wizard.country_region')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 9rem"
          >
            <template #body="{ data }">
              <span>{{ data.country || "-" }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('ecolabels.filters.search')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>

          <Column
            v-if="isColVisible('period')"
            field="period"
            :header="t('ecolabels.certificates.period_col')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 14rem"
          >
            <template #body="{ data }">
              <span v-if="data.period_start && data.period_end">
                {{ formatDate(data.period_start) }} -
                {{ formatDate(data.period_end) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('ecolabels.filters.search')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>

          <Column
            v-if="isColVisible('purpose')"
            field="purpose"
            :header="t('ecolabels.upload_wizard.purpose')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 10rem"
          >
            <template #body="{ data }">
              <span>{{ data.purpose || "-" }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('ecolabels.filters.search')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>

          <Column
            v-if="isColVisible('notes')"
            field="notes"
            :header="t('ecolabels.upload_wizard.notes')"
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 14rem"
          >
            <template #body="{ data }">
              <span :title="data.notes || '-'">{{
                truncateText(data.notes)
              }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('ecolabels.filters.search')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>

          <Column
            v-if="isColVisible('total_mwh')"
            field="total_mwh"
            :header="t('ecolabels.certificates.total_mwh_col')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 8rem"
          >
            <template #body="{ data }">
              <span v-if="data.total_mwh">{{ data.total_mwh }} MWh</span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('ecolabels.filters.capacity_placeholder')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>

          <Column
            v-if="isColVisible('devices_count')"
            field="devices_count"
            :header="t('ecolabels.certificates.devices_col')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 7rem"
          >
            <template #body="{ data }">
              <span v-if="data.devices_count > 0" class="badge bg-secondary">
                {{ data.devices_count }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('ecolabels.filters.search')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>

          <Column
            v-if="isColVisible('status')"
            field="status"
            :header="t('ecolabels.certificates.status_col')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            headerStyle="min-width: 9rem"
          >
            <template #body="{ data }">
              <span
                class="badge rounded-pill"
                :class="statementStatusBadgeClass(data.status)"
              >
                {{ formatStatus(data.status) }}
              </span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <MultiSelect
                v-model="filterModel.value"
                :options="statusFilterOptions"
                optionLabel="label"
                optionValue="value"
                :placeholder="t('common.filter')"
                class="p-column-filter w-100"
                display="chip"
                appendTo="body"
                :focusOnHover="false"
                @change="filterCallback()"
              >
                <template #value="{ value, placeholder }">
                  <span
                    v-if="value && value.length"
                    class="text-primary fw-bold"
                  >
                    {{ t("common.filter") }}
                  </span>
                  <span v-else>{{ placeholder }}</span>
                </template>
              </MultiSelect>
            </template>
          </Column>

          <Column
            :header="t('ecolabels.certificates.actions_col')"
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            :exportable="false"
            headerStyle="min-width: 14rem"
          >
            <template #body="{ data }">
              <div class="btn-group btn-group-sm">
                <button
                  v-if="data.statement_upload_id"
                  class="btn btn-outline-secondary"
                  :title="t('ecolabels.certificates.download_statement')"
                  @click="downloadStatement(data)"
                >
                  <font-awesome-icon icon="download" />
                </button>

                <button
                  v-if="data.status !== 'APPROVED' || isAdmin"
                  class="btn btn-outline-primary"
                  :title="t('ecolabels.certificates.edit_statement')"
                  @click="$emit('manage-devices', data)"
                >
                  <font-awesome-icon icon="edit" />
                </button>

                <button
                  v-if="isAdmin && data.status === 'PENDING'"
                  class="btn btn-outline-success"
                  :disabled="approving === data.id"
                  :title="t('ecolabels.certificates.approve')"
                  @click="handleApprove(data)"
                >
                  <span
                    v-if="approving === data.id"
                    class="spinner-border spinner-border-sm"
                  />
                  <font-awesome-icon v-else icon="check" />
                </button>

                <button
                  v-if="isAdmin && data.status === 'PENDING'"
                  class="btn btn-outline-danger"
                  :disabled="rejecting === data.id"
                  :title="t('ecolabels.certificates.reject')"
                  @click="handleReject(data)"
                >
                  <span
                    v-if="rejecting === data.id"
                    class="spinner-border spinner-border-sm"
                  />
                  <font-awesome-icon v-else icon="times" />
                </button>

                <button
                  v-if="isAdmin && data.status === 'APPROVED'"
                  class="btn btn-outline-success"
                  :disabled="generating === data.id"
                  :title="
                    data.certificate_upload_id
                      ? t('ecolabels.certificates.download_certificate')
                      : t('ecolabels.certificates.generate')
                  "
                  @click="handleCertificateAction(data)"
                >
                  <span
                    v-if="generating === data.id"
                    class="spinner-border spinner-border-sm"
                  />
                  <font-awesome-icon v-else icon="file-pdf" />
                </button>

                <button
                  v-else-if="data.certificate_upload_id"
                  class="btn btn-success"
                  :title="t('ecolabels.certificates.download_certificate')"
                  @click="downloadCertificate(data)"
                >
                  <font-awesome-icon icon="file-pdf" />
                </button>
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatDate, formatDateTimeIntoDate } from "@/composables/formatUtils";
import { useFileDownload } from "@/composables/useFileDownload";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { statementStatusBadgeClass } from "@/ecolabels/statementStatus";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import DebouncedTextFilter from "../DebouncedTextFilter.vue";
import MultiSelect from "primevue/multiselect";
import { useLocalStorage } from "@vueuse/core";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { ecolabelsApi } from "../api/ecolabelsApi";
import type { CancellationStatement } from "../api/ecolabelsTypes";

const emit = defineEmits<{
  "open-upload": [];
  "manage-devices": [stmt: CancellationStatement];
  refresh: [];
}>();

const { t } = useI18n();

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const { downloadCompanyDocument } = useFileDownload();

const approving = ref<number | null>(null);
const rejecting = ref<number | null>(null);
const generating = ref<number | null>(null);
const props = defineProps<{
  statements: CancellationStatement[];
  loading: boolean;
  isAdmin: boolean;
}>();

type CertificateColumnKey =
  | "file_name"
  | "created_at"
  | "beneficiary_name"
  | "seller_company"
  | "registry_code"
  | "country"
  | "period"
  | "purpose"
  | "notes"
  | "total_mwh"
  | "devices_count"
  | "status";

const allColumns: Record<
  CertificateColumnKey,
  { header: string; defaultVisible: boolean }
> = {
  file_name: {
    header: "ecolabels.certificates.file_name",
    defaultVisible: false,
  },
  created_at: {
    header: "ecolabels.certificates.uploaded",
    defaultVisible: true,
  },
  seller_company: {
    header: "ecolabels.upload_wizard.seller_company",
    defaultVisible: true,
  },
  beneficiary_name: {
    header: "ecolabels.upload_wizard.beneficiary_name",
    defaultVisible: true,
  },
  registry_code: {
    header: "ecolabels.upload_wizard.registry_code",
    defaultVisible: false,
  },
  country: {
    header: "ecolabels.upload_wizard.country_region",
    defaultVisible: false,
  },
  period: {
    header: "ecolabels.certificates.period_col",
    defaultVisible: false,
  },
  purpose: { header: "ecolabels.upload_wizard.purpose", defaultVisible: false },
  notes: { header: "ecolabels.upload_wizard.notes", defaultVisible: false },
  total_mwh: {
    header: "ecolabels.certificates.total_mwh_col",
    defaultVisible: true,
  },
  devices_count: {
    header: "ecolabels.certificates.devices_col",
    defaultVisible: true,
  },
  status: { header: "ecolabels.certificates.status_col", defaultVisible: true },
};

const defaultVisibleColumns = (
  Object.entries(allColumns) as Array<
    [CertificateColumnKey, { defaultVisible: boolean }]
  >
)
  .filter(([, value]) => value.defaultVisible)
  .map(([key]) => key);

const visibleColumns = useLocalStorage<CertificateColumnKey[]>(
  "ecolabel-certificates-visible-columns-v2",
  defaultVisibleColumns
);

const columnOptions = computed(() =>
  (
    Object.entries(allColumns) as Array<
      [CertificateColumnKey, { header: string }]
    >
  ).map(([key, value]) => ({
    value: key,
    label: t(value.header),
  }))
);

const isColVisible = (column: CertificateColumnKey) =>
  visibleColumns.value.includes(column);

const tableMinWidth = computed(
  () => `${Math.max(visibleColumns.value.length * 12, 70)}rem`
);

const tableRows = computed(() =>
  props.statements.map((stmt) => ({
    ...stmt,
    seller_company: stmt.company_name ?? "",
    period:
      stmt.period_start && stmt.period_end
        ? `${formatDate(stmt.period_start)} - ${formatDate(stmt.period_end)}`
        : "",
    devices_count: stmt.devices.length,
  }))
);
const statusFilterOptions = [
  { label: "Pending", value: "PENDING" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
];

const tableFilters = ref({
  file_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  created_at: { value: null, matchMode: FilterMatchMode.CONTAINS },
  beneficiary_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  seller_company: { value: null, matchMode: FilterMatchMode.CONTAINS },
  registry_code: { value: null, matchMode: FilterMatchMode.CONTAINS },
  country: { value: null, matchMode: FilterMatchMode.CONTAINS },
  period: { value: null, matchMode: FilterMatchMode.CONTAINS },
  purpose: { value: null, matchMode: FilterMatchMode.CONTAINS },
  notes: { value: null, matchMode: FilterMatchMode.CONTAINS },
  total_mwh: { value: null, matchMode: FilterMatchMode.CONTAINS },
  devices_count: { value: null, matchMode: FilterMatchMode.CONTAINS },
  status: { value: null, matchMode: FilterMatchMode.IN },
});

const formatStatus = (status: string) =>
  status.charAt(0) + status.slice(1).toLowerCase();

const truncateText = (text?: string | null) => {
  if (!text) return "-";
  if (text.length <= 60) return text;
  return `${text.slice(0, 57)}...`;
};

const handleApprove = async (stmt: CancellationStatement) => {
  approving.value = stmt.id;
  try {
    await ecolabelsApi.approveStatement(stmt.id);
    globalSuccessHandler(t("ecolabels.certificates.approved_success"), "");
    emit("refresh");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    approving.value = null;
  }
};

const handleReject = async (stmt: CancellationStatement) => {
  if (!confirm(t("ecolabels.certificates.confirm_reject"))) return;
  rejecting.value = stmt.id;
  try {
    await ecolabelsApi.rejectStatement(stmt.id);
    globalSuccessHandler(t("ecolabels.certificates.rejected_success"), "");
    emit("refresh");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    rejecting.value = null;
  }
};

const handleGenerateCertificate = async (stmt: CancellationStatement) => {
  generating.value = stmt.id;
  try {
    const response = await ecolabelsApi.generateStatementCertificate(stmt.id);
    const filename = `ecolabel-certificate-${stmt.id}-${new Date().toISOString().split("T")[0]}.pdf`;
    downloadFromApiResponse(
      response.data,
      response.headers,
      filename,
      "application/pdf"
    );
    globalSuccessHandler(t("ecolabels.certificates.certificate_generated"), "");
    emit("refresh");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    generating.value = null;
  }
};

const handleCertificateAction = async (stmt: CancellationStatement) => {
  if (stmt.certificate_upload_id) {
    await downloadCertificate(stmt);
    return;
  }
  await handleGenerateCertificate(stmt);
};

const downloadStatement = async (stmt: CancellationStatement) => {
  if (!stmt.statement_upload_id) return;
  await downloadCompanyDocument(stmt.company_id, stmt.statement_upload_id, {
    filename: stmt.file_name,
  });
};

const downloadCertificate = async (stmt: CancellationStatement) => {
  if (!stmt.certificate_upload_id) return;
  await downloadCompanyDocument(stmt.company_id, stmt.certificate_upload_id, {
    filename: `ecolabel-certificate-${stmt.id}.pdf`,
  });
};
</script>

<style scoped lang="scss">
:deep(.ecolabel-certificates-datatable) {
  .p-datatable-thead > tr > th {
    background-color: var(--phoenix-body-bg);
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--phoenix-body-color);
    border-bottom: 1px solid var(--phoenix-border-color);
  }

  .p-datatable-tbody > tr {
    &:hover {
      background-color: rgba(var(--phoenix-primary-rgb), 0.04);
    }

    > td {
      border-bottom: 1px solid var(--phoenix-border-color);
      font-size: 0.875rem;
    }
  }

  .p-column-filter {
    font-size: 0.8rem;
  }
}

.column-selector {
  min-width: 200px;
  max-width: 240px;
}
</style>
